import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  RefreshCw,
  Trophy,
  CheckCircle2,
  Clock,
  Layers,
  CalendarDays,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type Pool = {
  id: string;
  name: string;
  status: string;
  total_games: number;
  lottery_id: string;
  check_from_contest: number | null;
  lotteries: { name: string; slug: string; numbers_drawn: number } | null;
};

type PoolGame = {
  id: string;
  pool_id: string;
  position: number;
  numbers: number[];
  last_hits: number | null;
  last_contest: number | null;
};

type Draw = {
  id: string;
  contest_number: number;
  draw_date: string | null;
  lottery_id: string;
};

export default function Conferencias() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [recheckingPool, setRecheckingPool] = useState<string | null>(null);

  const { data: pools, isLoading: loadingPools } = useQuery({
    queryKey: ["pools-conferencias", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pools")
        .select("id, name, status, total_games, lottery_id, check_from_contest, lotteries(name, slug, numbers_drawn)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Pool[];
    },
  });

  const { data: games, isLoading: loadingGames } = useQuery({
    queryKey: ["pool-games-all", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pool_games")
        .select("id, pool_id, position, numbers, last_hits, last_contest");
      if (error) throw error;
      return (data ?? []) as PoolGame[];
    },
  });

  const lotteryIds = useMemo(
    () => Array.from(new Set((pools ?? []).map((p) => p.lottery_id))),
    [pools],
  );

  const { data: draws } = useQuery({
    queryKey: ["recent-draws", lotteryIds],
    enabled: lotteryIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("draws")
        .select("id, contest_number, draw_date, lottery_id")
        .in("lottery_id", lotteryIds)
        .order("contest_number", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as Draw[];
    },
  });

  const recheck = useMutation({
    mutationFn: async ({ poolId, drawId }: { poolId: string; drawId: string }) => {
      const { error } = await supabase.rpc("check_pool_against_draw", {
        _pool_id: poolId,
        _draw_id: drawId,
      });
      if (error) throw error;
    },
    onMutate: ({ poolId }) => setRecheckingPool(poolId),
    onSuccess: () => {
      toast.success("Conferência atualizada");
      qc.invalidateQueries({ queryKey: ["pool-games-all"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao reverificar"),
    onSettled: () => setRecheckingPool(null),
  });

  // Agrupa estatísticas por bolão
  const poolStats = useMemo(() => {
    if (!pools || !games) return [];
    return pools.map((pool) => {
      const poolGames = games.filter((g) => g.pool_id === pool.id);
      const checked = poolGames.filter((g) => g.last_hits != null && g.last_contest === pool.check_from_contest);
      const bestGame = checked.reduce<PoolGame | null>(
        (best, g) => (g.last_hits! > (best?.last_hits ?? -1) ? g : best),
        null,
      );
      const lastContest = checked.reduce<number | null>(
        (max, g) => (g.last_contest != null && g.last_contest > (max ?? -1) ? g.last_contest : max),
        null,
      );
      const drawnCount = pool.lotteries?.numbers_drawn ?? 6;
      const isPrize = (bestGame?.last_hits ?? 0) >= drawnCount - 2;
      return {
        pool,
        totalGames: poolGames.length,
        checkedCount: checked.length,
        bestHits: bestGame?.last_hits ?? null,
        lastContest,
        isPrize,
        coverage: poolGames.length > 0 ? checked.length / poolGames.length : 0,
      };
    });
  }, [pools, games]);

  // Agrupa concursos por loteria
  const drawsByLottery = useMemo(() => {
    const map = new Map<string, Draw[]>();
    (draws ?? []).forEach((d) => {
      if (!map.has(d.lottery_id)) map.set(d.lottery_id, []);
      map.get(d.lottery_id)!.push(d);
    });
    return map;
  }, [draws]);

  const totalChecked = poolStats.reduce((s, p) => s + p.checkedCount, 0);
  const totalGames = poolStats.reduce((s, p) => s + p.totalGames, 0);
  const prizedCount = poolStats.filter((p) => p.isPrize).length;

  const isLoading = loadingPools || loadingGames;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!pools || pools.length === 0) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/app/boloes"><ArrowLeft className="mr-1 h-4 w-4" /> Bolões</Link>
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Layers className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="font-serif text-xl">Nenhum bolão para conferir</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Crie um bolão e ele será verificado automaticamente assim que um novo concurso for publicado.
            </p>
            <Button asChild className="mt-6">
              <Link to="/app/boloes/novo">Criar bolão</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/app/boloes"><ArrowLeft className="mr-1 h-4 w-4" /> Bolões</Link>
        </Button>
        <h1 className="font-serif text-3xl">Conferências automáticas</h1>
        <p className="text-sm text-muted-foreground">
          Acompanhe o status de verificação dos seus bolões a cada novo concurso publicado.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Bolões ativos" value={String(pools.length)} icon={<Layers className="h-4 w-4" />} />
        <StatCard
          label="Jogos verificados"
          value={`${totalChecked} / ${totalGames}`}
          icon={<CheckCircle2 className="h-4 w-4" />}
        />
        <StatCard
          label="Bolões premiados"
          value={String(prizedCount)}
          icon={<Trophy className="h-4 w-4" />}
          highlight={prizedCount > 0}
        />
        <StatCard
          label="Último concurso"
          value={
            draws && draws.length > 0
              ? `#${draws[0].contest_number}`
              : "—"
          }
          icon={<CalendarDays className="h-4 w-4" />}
        />
      </div>

      <Tabs defaultValue="status" className="space-y-4">
        <TabsList>
          <TabsTrigger value="status">Status por bolão</TabsTrigger>
          <TabsTrigger value="historico">Histórico de concursos</TabsTrigger>
        </TabsList>

        <TabsContent value="status">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-lg">Status atual</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bolão</TableHead>
                      <TableHead>Loteria</TableHead>
                      <TableHead className="text-center">Cobertura</TableHead>
                      <TableHead className="text-center">Último concurso</TableHead>
                      <TableHead className="text-center">Melhor acerto</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {poolStats.map(({ pool, totalGames, checkedCount, bestHits, lastContest, isPrize, coverage }) => {
                      const lastDrawForLottery = drawsByLottery.get(pool.lottery_id)?.find((draw) => draw.contest_number === pool.check_from_contest);
                      return (
                        <TableRow key={pool.id}>
                          <TableCell>
                            <Link to={`/app/boloes/${pool.id}`} className="font-medium text-foreground hover:text-primary">
                              {pool.name}
                            </Link>
                            <div className="text-[11px] text-muted-foreground">{totalGames} jogos · Concurso #{pool.check_from_contest ?? "—"}</div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{pool.lotteries?.name}</TableCell>
                          <TableCell className="text-center">
                            <div className="text-xs font-medium">
                              {checkedCount}/{totalGames}
                            </div>
                            <div className="mx-auto mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-lt-bg2">
                              <div
                                className={cn(
                                  "h-full rounded-full transition-all",
                                  coverage === 1 ? "bg-lt-teal" : "bg-primary/60",
                                )}
                                style={{ width: `${coverage * 100}%` }}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {lastContest != null ? (
                              <span>#{lastContest}</span>
                            ) : (
                              <span className="text-muted-foreground">aguardando</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {bestHits != null ? (
                              <span
                                className={cn(
                                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                                  isPrize
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-lt-teal/20 text-lt-teal",
                                )}
                              >
                                {isPrize && <Trophy className="h-3 w-3" />}
                                {bestHits} acertos
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!lastDrawForLottery || recheckingPool === pool.id}
                              onClick={() =>
                                lastDrawForLottery &&
                                recheck.mutate({ poolId: pool.id, drawId: lastDrawForLottery.id })
                              }
                            >
                              <RefreshCw
                                className={cn(
                                  "mr-1.5 h-3.5 w-3.5",
                                  recheckingPool === pool.id && "animate-spin",
                                )}
                              />
                              Reverificar
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4 border-lt-teal/30 bg-lt-teal/5">
            <CardContent className="flex items-start gap-3 pt-6">
              <Clock className="mt-0.5 h-5 w-5 text-lt-teal" />
              <div className="text-sm">
                <div className="font-medium">Verificação automática ativa</div>
                <p className="text-xs text-muted-foreground">
                  Cada novo concurso publicado dispara a conferência de todos os bolões ativos.
                  Se algum jogo cair em faixa de prêmio, você recebe uma notificação imediata.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif text-lg">Concursos recentes</CardTitle>
            </CardHeader>
            <CardContent>
              {!draws || draws.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  <AlertCircle className="mx-auto mb-2 h-6 w-6" />
                  Nenhum concurso encontrado para suas loterias.
                </div>
              ) : (
                <div className="space-y-6">
                  {Array.from(drawsByLottery.entries()).map(([lotteryId, lotteryDraws]) => {
                    const pool = pools.find((p) => p.lottery_id === lotteryId);
                    return (
                      <div key={lotteryId}>
                        <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">
                          {pool?.lotteries?.name}
                        </div>
                        <div className="space-y-1">
                          {lotteryDraws.slice(0, 10).map((d) => {
                            const checkedHere = (games ?? []).filter(
                              (g) => g.last_contest === d.contest_number &&
                                pools.find((p) => p.id === g.pool_id)?.lottery_id === lotteryId,
                            );
                            const maxHits = checkedHere.reduce(
                              (m, g) => Math.max(m, g.last_hits ?? 0),
                              0,
                            );
                            return (
                              <div
                                key={d.id}
                                className="flex items-center justify-between rounded-md border border-lt-border bg-lt-bg2 px-3 py-2 text-[13px]"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="font-mono text-xs text-muted-foreground">
                                    #{d.contest_number}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {d.draw_date
                                      ? new Date(d.draw_date).toLocaleDateString("pt-BR")
                                      : "—"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  {checkedHere.length > 0 ? (
                                    <>
                                      <span className="text-[11px] text-muted-foreground">
                                        {checkedHere.length} jogos conferidos
                                      </span>
                                      {maxHits > 0 && (
                                        <span className="rounded-full bg-lt-teal/20 px-2 py-0.5 text-[10px] font-medium text-lt-teal">
                                          melhor: {maxHits}
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-[11px] text-muted-foreground">
                                      sem conferência
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  highlight,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <Card className={cn(highlight && "border-primary/40 bg-lt-gold-bg")}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="mt-1 font-serif text-2xl">{value}</div>
      </CardContent>
    </Card>
  );
}